// ctx/workerClient are intentionally `unknown` — callers pass
// differently-typed contexts (worker, frontend, mocks); we only read
// ctx.headers and call workerClient.call/.callByName at runtime. Helpers
// below narrow/cast at the single call site.
type Ctx = unknown
type WorkerClientLike = unknown

export interface CallOptions {
  headers?: Record<string, unknown>
  [k: string]: unknown
}

const ctxHeaders = (ctx: Ctx): Record<string, unknown> => {
  return (ctx as { headers?: Record<string, unknown> })?.headers || {}
}

/**
 * Forward a call to another worker, passing auth context
 */
export const forwardCall = async (
  ctx: Ctx,
  workerClient: WorkerClientLike,
  targetPublicKey: string,
  method: string,
  params: unknown[],
  options: CallOptions = {},
): Promise<unknown> => {
  if (!workerClient) {
    throw new Error('WorkerClient not available')
  }

  return (workerClient as { call: (...a: unknown[]) => Promise<unknown> }).call(
    targetPublicKey,
    method,
    params,
    {
      ...options,
      headers: {
        ...ctxHeaders(ctx),
        ...options.headers,
      },
    },
  )
}

/**
 * Forward a call to another worker by name, passing auth context
 */
export const forwardCallByName = async (
  ctx: Ctx,
  workerClient: WorkerClientLike,
  workerName: string,
  method: string,
  params: unknown[],
  options: CallOptions = {},
): Promise<unknown> => {
  if (!workerClient) {
    throw new Error('WorkerClient not available')
  }

  return (workerClient as { callByName: (...a: unknown[]) => Promise<unknown> }).callByName(
    workerName,
    method,
    params,
    {
      ...options,
      headers: {
        ...ctxHeaders(ctx),
        ...options.headers,
      },
    },
  )
}
